import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Loading from '@/components/Loading';
import { getCalculationsAndMeasurements } from '@/helpers';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import type { AnimalAlertV1 } from '@/model/Alert.model';
import type { Animal as AnimalModel, AnimalApiId } from '@/model/Animal.model';
import type { Study as StudyModel, StudyApiId } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { Alert, type AlertSettings, type AlertUpdateEventHandler } from '@/utils/alerts/Alert';
import { sortDescByDateStrProperty } from '@/utils/array.utils';
import type { Dispatch, FC } from 'react';
import { useEffect, useMemo } from 'react';
import NoDataCard from '../../../NoDataCard';
import type { AnimalAlertAction } from '../Show.utils';

interface AlertsProps {
  animal: AnimalModel;
  study: StudyModel;
  dispatch: Dispatch<AnimalAlertAction>;
}

interface SortedAlerts {
  resolvedAlerts: Array<AnimalAlertV1>;
  unresolvedAlerts: Array<AnimalAlertV1>;
}

export const Alerts: FC<AlertsProps> = ({ study, animal, dispatch }) => {
  const isWriteUser = Auth.isWriteUserForStudy(study);

  const alertSettings: AlertSettings = {
    displayDeferControl: false,
    displayCreatedAt: false,
    displayHeader: false,
    displayTriggeredBy: true,
    displayAlertState: false,
    displayResolvedTick: true,
    displayResolveControl: isWriteUser,
  };

  const {
    loading: alertsLoading,
    response: alertsResponse,
    invoke: reloadAlerts,
  } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/animals/{animalApiId}/alerts',
    path: { studyApiId: study.api_id as StudyApiId, animalApiId: animal.api_id as AnimalApiId },
    options: { onError: { throw: false, capture: true, toast: true, slug: 'subject-show-fetch-alerts' } },
  });

  const isError = alertsResponse?.type === 'error';
  const alerts = isError ? undefined : (alertsResponse?.body as Array<AnimalAlertV1> | undefined);

  useEffect(() => {
    if (_isNotEmpty(alerts)) {
      dispatch({ type: 'SET_ALERTS', data: alerts });
    }
  }, [alerts]);

  const updateEventHandler: AlertUpdateEventHandler = async ({ type }) => {
    if (type === 'resolved' || type === 'deferred') {
      await reloadAlerts();
    }
  };

  const { resolvedAlerts, unresolvedAlerts } = useMemo<SortedAlerts>(
    () =>
      (alerts ?? [])
        .slice()
        .sort(sortDescByDateStrProperty('created_at'))
        .reduce<SortedAlerts>(
          ({ resolvedAlerts, unresolvedAlerts }, alert) =>
            _notNil(alert.resolved_at)
              ? { unresolvedAlerts, resolvedAlerts: [...resolvedAlerts, alert] }
              : { resolvedAlerts, unresolvedAlerts: [...unresolvedAlerts, alert] },
          { resolvedAlerts: [], unresolvedAlerts: [] }
        ),
    [alerts]
  );
  const calculationMap = useMemo(
    () => getCalculationsAndMeasurements(study?.settings?.calculations),
    [study?.settings?.calculations]
  );

  if (alertsLoading) {
    return <Loading />;
  }
  if (isError) {
    return (
      <div className="ui-card">
        <div className="ma4">
          <ApiErrorBanner errorType={ApiErrorType.FETCH} error={alertsResponse?.error} />
        </div>
      </div>
    );
  }
  if (_isEmpty(alerts)) {
    return (
      <div className="ui-card">
        <div className="ma4">
          <NoDataCard className="tc" title="No alerts to display" dark />
        </div>
      </div>
    );
  }
  return (
    <div>
      {_isNotEmpty(unresolvedAlerts) && (
        <div className="ph4 pv3 bg-white br2 ba b--moon-gray mb4">
          <h3 className="mt3 mb1">Unresolved</h3>
          <div className="flex flex-column g3">
            {unresolvedAlerts.map((alert) => (
              <Alert
                key={alert.id}
                alert={alert}
                studyApiId={study.api_id}
                animalApiId={animal.api_id}
                calculation={calculationMap.get(alert.calculation)}
                settings={alertSettings}
                onUpdate={updateEventHandler}
              />
            ))}
          </div>
        </div>
      )}

      {_isNotEmpty(resolvedAlerts) && (
        <div className="ph4 pv3 bg-white br2 ba b--moon-gray mb4">
          <h3 className="mt3 mb1">Resolved</h3>
          <div className="flex flex-column g3">
            {resolvedAlerts.map((alert) => (
              <Alert
                key={alert.id}
                alert={alert}
                studyApiId={study.api_id}
                animalApiId={animal.api_id}
                calculation={calculationMap.get(alert.calculation)}
                animalName={animal.name}
                cageName={animal?.cage?.name}
                settings={alertSettings}
                onUpdate={updateEventHandler}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
